'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useConversation } from '@elevenlabs/react';
import { usePrivy } from '@privy-io/react-auth';
import type { ConversationDetailDTO, SessionMode } from '@jas/shared';
import { BudgetExhaustedError, fetchSessionToken, postChatDebit, postHeartbeat } from '@/lib/api';
import { appendMessage, createConversation, getConversation } from '@/lib/conversations';
import { track } from '@/lib/events';
import { getFingerprint } from '@/lib/fingerprint';
import {
  useBeforeUnloadGuard,
  useNetworkStatus,
  useTabVisibilityTelemetry,
} from '@/lib/resilience';
import { AuthButton } from './auth-button';
import { ChatSessionView } from './chat-session-view';
import { ConnectingView } from './connecting-view';
import { ErrorView } from './error-view';
import { HistoryDrawer } from './history-drawer';
import { IdleView } from './idle-view';
import { MicPrePrompt } from './mic-pre-prompt';
import { SessionEndBanner } from './session-end-banner';
import { SessionView } from './session-view';
import { WaitlistModal } from './waitlist-modal';

// ── Types ──────────────────────────────────────────────────────────────────────

type Phase =
  | 'idle'
  | 'priming'
  | 'connecting'
  | 'live'
  | 'ending'
  | 'rate-limited'
  | 'session-ended'
  | 'error';

const HEARTBEAT_MS = 15_000;
const MOCK_URL = 'MOCK_SIGNED_URL_AGENT_NOT_CONFIGURED';
// Fallback only — the canonical value comes from SessionTokenResponse
// (server-authoritative). We initialise the FE state to this so the
// landing CTA shows roughly-right copy before any token has been
// issued; once a session starts, the server-supplied value replaces
// it. The fallback matches the default in apps/api env validation.
const CHAT_TURN_SECONDS_FALLBACK = 30;

// ── Root component ─────────────────────────────────────────────────────────────

export function VoicePanel() {
  const [phase, setPhase] = useState<Phase>('idle');
  const [mode, setMode] = useState<SessionMode>('voice');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [displaySeconds, setDisplaySeconds] = useState<number | null>(null);
  const [budgetSeconds, setBudgetSeconds] = useState<number>(300);
  const [turns, setTurns] = useState<{ id: string; role: 'user' | 'satoshi'; text: string }[]>([]);
  const [scrolled, setScrolled] = useState(false);
  const [showEndBanner, setShowEndBanner] = useState(false);
  // Tracks whether we've sent a user-message in chat mode and are
  // waiting for Satoshi's response. Toggled true by handleChatSend,
  // back to false on the first `satoshi`-role onMessage.
  const [chatAwaitingReply, setChatAwaitingReply] = useState(false);
  // Mirrors SessionTokenResponse.chatTurnSeconds. Initialised to the
  // fallback so the FE can compute "messages remaining" even before a
  // token is issued; replaced with the server's value once a session
  // starts.
  const [chatTurnSeconds, setChatTurnSeconds] = useState<number>(CHAT_TURN_SECONDS_FALLBACK);
  // Set when the lazy createConversation (fired on the user's first
  // message) fails. Surfaced in the session sidebar so signed-in users
  // know this conversation won't appear in history; failure isn't
  // fatal, the live session keeps running.
  const [persistError, setPersistError] = useState<string | null>(null);
  const sessionTokenRef = useRef<string | null>(null);
  const firstWordAtRef = useRef<number | null>(null);
  const sessionStartAtRef = useRef<number | null>(null);
  // Conversation persistence — set lazily, when the signed-in user
  // sends their first message (not on session start). Every onMessage
  // after that POSTs to /api/conversations/:id/messages so the history
  // drawer can rebuild the transcript later.
  const conversationIdRef = useRef<string | null>(null);
  // State mirror of `conversationIdRef` so we can pass the current id
  // down to the in-session views (which need to re-render when it
  // changes — to refetch the sidebar "RECENT" list and exclude the
  // active row). Always keep these two in sync: when the ref is set,
  // also call `setCurrentConversationId`; same on clear.
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  // When resuming a past conversation, we stash a single contextual-
  // update payload here and flush it on the FIRST onConnect after the
  // resume kicks. Using a ref (not state) because the ElevenLabs SDK
  // captures onConnect at hook init — state reads inside would be
  // stale by the time the SDK fires it.
  const resumeContextRef = useRef<string | null>(null);
  // Set true by proceedResume immediately before it tears down the
  // current live session to switch into a different conversation.
  // That endSession() fires the SDK's onDisconnect; without this flag
  // onDisconnect treats it as a natural end → 'session-ended' → the
  // session-ended effect runs reset(), which wipes `turns` and nulls
  // conversationIdRef. The resumed conversation then opens blank and
  // its later turns stop persisting (so it never gets auto-titled).
  // The next onDisconnect consumes this flag and is suppressed.
  const resumingRef = useRef(false);
  // Lazy persistence gate. Flips true on the user's first message;
  // until then Satoshi's opening greeting is shown live but never
  // persisted — a session the user never engages with leaves no
  // "Untitled" history row. Reset on start/reset, forced true on
  // resume (the conversation already exists).
  const userTurnSeenRef = useRef(false);
  // Latest-ref mirror of `mode` so the async persist drainer can read
  // the session mode when it lazily creates the conversation row,
  // without `mode` churning the drainer's useCallback identity.
  const modeRef = useRef(mode);
  modeRef.current = mode;

  const { authenticated, getAccessToken } = usePrivy();

  const isCallActive = phase === 'live' || phase === 'connecting' || phase === 'ending';
  useBeforeUnloadGuard(isCallActive);
  useTabVisibilityTelemetry(phase === 'live');
  const online = useNetworkStatus();

  // Navbar scroll-aware transparency on landing
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // When leaving the landing page, snap scroll to top and lock body overflow so
  // the session view can never be scrolled (stale landing-page scroll position
  // would reveal blank space below the session content otherwise).
  useEffect(() => {
    const isLanding = phase === 'idle';
    if (!isLanding) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      document.body.style.overflow = 'hidden';
    }
    return () => { document.body.style.overflow = ''; };
  }, [phase]);

  // Snap display timer to server value whenever a heartbeat/token arrives
  useEffect(() => {
    if (remainingSeconds !== null) setDisplaySeconds(remainingSeconds);
  }, [remainingSeconds]);

  // Tick down every second while live — VOICE ONLY. The heartbeat corrects drift
  // every 15s. Chat mode must NOT run this timer: in chat the budget is debited
  // per-message, not continuously. Running it in chat caused displaySeconds to
  // drift down 1/s even without sends, so messagesLeft dropped 1 every 30s of
  // inactivity and appeared to skip counts around message sends (the 9→7 bug).
  useEffect(() => {
    if (phase !== 'live' || mode !== 'voice') return;
    const id = window.setInterval(() => {
      setDisplaySeconds((prev) => (prev !== null ? Math.max(0, prev - 1) : null));
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase, mode]);

  /**
   * Buffered persistence queue. Each turn is enqueued; a serial
   * drainer attempts to flush. On API failure the failed item stays
   * at the head of the queue and the drainer bails — the next enqueue
   * triggers another drain attempt. This survives transient network
   * blips without losing turns.
   *
   * The conversation row is created lazily, by the drainer, on the
   * first turn it actually has to flush. Combined with persistTurn's
   * gate (it ignores Satoshi's pre-user greeting), this means a
   * session the user never speaks in produces no history row at all.
   *
   * Refs (not state) because the drainer is async and we don't want
   * re-renders to interfere with the queue. Lost only if the user
   * closes the tab before the queue drains — acceptable for Phase B.
   */
  const persistQueueRef = useRef<Array<{ role: 'user' | 'satoshi'; text: string }>>([]);
  const persistDrainingRef = useRef(false);

  const drainPersistQueue = useCallback(async () => {
    if (persistDrainingRef.current) return;
    if (!authenticated || persistQueueRef.current.length === 0) return;
    persistDrainingRef.current = true;
    try {
      while (persistQueueRef.current.length > 0) {
        // Lazy creation: the first flush with something queued creates
        // the conversation row. persistTurn only enqueues once the
        // user has actually spoken, so this never fires for a
        // greeting-only session — no row, no "Untitled" history entry.
        let conversationId = conversationIdRef.current;
        if (!conversationId) {
          const token = await getAccessToken();
          if (!token) {
            setPersistError('No access token — history not saved.');
            return;
          }
          try {
            const conv = await createConversation(token, { mode: modeRef.current });
            conversationIdRef.current = conv.id;
            conversationId = conv.id;
            setCurrentConversationId(conv.id);
            setPersistError(null);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('createConversation failed; turns will not persist', err);
            setPersistError('History save failed.');
            return; // bail; queued turns retry on the next enqueue
          }
        }
        const next = persistQueueRef.current[0]!;
        try {
          const token = await getAccessToken();
          if (!token) return; // bail; next call will retry
          await appendMessage(token, conversationId, next);
          persistQueueRef.current.shift();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('persistTurn failed; will retry on next turn', err);
          return; // bail; remaining items stay queued
        }
      }
    } finally {
      persistDrainingRef.current = false;
    }
  }, [authenticated, getAccessToken]);

  const persistTurn = useCallback(
    (role: 'user' | 'satoshi', text: string) => {
      if (!authenticated) return;
      if (role === 'user') userTurnSeenRef.current = true;
      // Skip everything before the user's first message: Satoshi's
      // opening greeting must not, on its own, create a history row.
      // Once the user has spoken (userTurnSeenRef) — or we're resuming
      // an existing conversation (conversationIdRef) — every turn is
      // enqueued, and the drainer creates the row lazily on first flush.
      if (!userTurnSeenRef.current && !conversationIdRef.current) return;
      persistQueueRef.current.push({ role, text });
      void drainPersistQueue();
    },
    [authenticated, drainPersistQueue],
  );

  const conversation = useConversation({
    onConnect: () => {
      setPhase('live');
      track('conversation_start', { mode });
      // Resume path: flush the prebuilt context to the agent so it
      // continues from where the user left off. sendContextualUpdate
      // is silent to the user (no transcript entry) but feeds the
      // model the prior turns as system-style context.
      const pending = resumeContextRef.current;
      if (pending) {
        resumeContextRef.current = null;
        try {
          conversationRef.current.sendContextualUpdate(pending);
          track('conversation_resumed', { mode });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('sendContextualUpdate failed during resume', err);
        }
      }
    },
    onDisconnect: () => {
      // A resume is tearing down the previous session to start a
      // different one — this disconnect is internal bookkeeping, not a
      // user-facing end. Consume the flag and skip the session-ended
      // transition (which would reset() and wipe the resumed state).
      if (resumingRef.current) {
        resumingRef.current = false;
        return;
      }
      setPhase((p) => (p === 'rate-limited' ? p : 'session-ended'));
      const startedAt = sessionStartAtRef.current;
      track('conversation_end', {
        mode,
        durationMs: startedAt ? Date.now() - startedAt : null,
      });
    },
    onError: (msg) => {
      setErrorMsg(typeof msg === 'string' ? msg : `${mode === 'chat' ? 'Chat' : 'Voice'} connection failed`);
      setPhase('error');
      track('page_error', { source: 'sdk', mode, message: typeof msg === 'string' ? msg : 'unknown' });
    },
    onMessage: (event) => {
      const e = event as { source?: 'user' | 'ai'; message?: string };
      if (!e?.message) return;
      const role: 'user' | 'satoshi' = e.source === 'ai' ? 'satoshi' : 'user';
      // Chat mode: the user's own messages are rendered + persisted
      // optimistically by handleChatSend (the ElevenLabs text channel
      // never echoes them back as a transcript). Ignore any user-role
      // event here so chat turns aren't double-rendered. Voice mode
      // still relies on onMessage for the user's ASR transcript.
      if (role === 'user' && modeRef.current === 'chat') return;
      if (role === 'satoshi' && firstWordAtRef.current === null && sessionStartAtRef.current) {
        firstWordAtRef.current = Date.now();
        track('first_word_latency', { mode, ms: firstWordAtRef.current - sessionStartAtRef.current });
      }
      // Clear the "Satoshi is composing…" indicator the moment his
      // first onMessage lands.
      if (role === 'satoshi') setChatAwaitingReply(false);
      const text = e.message ?? '';
      setTurns((prev) => [...prev, { id: crypto.randomUUID(), role, text }]);
      void persistTurn(role, text);
    },
  });

  // Cleanup on unmount — ref pattern avoids ending session on every re-render
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;
  useEffect(() => {
    return () => {
      if (sessionTokenRef.current) {
        try { conversationRef.current.endSession(); } catch { /* best-effort */ }
        track('session_aborted_unmount');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Heartbeat — only runs while live in voice mode. Chat mode debits
  // per-message via /api/session/chat-debit when the user sends, so
  // the heartbeat would over-debit.
  useEffect(() => {
    if (phase !== 'live' || mode !== 'voice' || !sessionTokenRef.current) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled || !sessionTokenRef.current) return;
      try {
        const r = await postHeartbeat(sessionTokenRef.current);
        if (cancelled) return;
        setRemainingSeconds(r.remainingSeconds);
        if (r.remainingSeconds <= 0) {
          conversation.endSession();
          setPhase('rate-limited');
          track('limit_hit', { trigger: 'heartbeat' });
        }
      } catch { /* network blip — next tick retries */ }
    };
    const id = window.setInterval(tick, HEARTBEAT_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [phase, mode, conversation]);

  // Natural session end: return to idle after a beat, show soft banner
  useEffect(() => {
    if (phase !== 'session-ended') return;
    const t = window.setTimeout(() => {
      reset();
      setShowEndBanner(true);
    }, 1400);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const start = useCallback(async () => {
    setErrorMsg(null);
    setPersistError(null);
    setTurns([]);
    conversationIdRef.current = null;
    resumingRef.current = false;
    userTurnSeenRef.current = false;
    persistQueueRef.current = [];
    setCurrentConversationId(null);
    setPhase('connecting');
    if (mode === 'voice') track('mic_granted');
    try {
      const fp = getFingerprint();
      sessionStartAtRef.current = Date.now();
      firstWordAtRef.current = null;
      track('session_start', { mode });
      const token = await fetchSessionToken(fp, undefined, mode);
      sessionTokenRef.current = token.sessionToken;
      setRemainingSeconds(token.remainingSeconds);
      setBudgetSeconds(token.budgetSeconds);
      // Server-supplied (canonical) — replaces the FE fallback. If the
      // env var ever changes server-side, the FE picks it up on the
      // next session start without a redeploy.
      setChatTurnSeconds(token.chatTurnSeconds);
      if (token.signedUrl === MOCK_URL || token.signedUrl.startsWith('MOCK_')) {
        setErrorMsg(
          'Agent not configured (no ELEVENLABS_AGENT_ID). The token endpoint, rate limiter, and waitlist are all wired — connect the agent in the ElevenLabs dashboard and paste the agent ID into the env vars.',
        );
        setPhase('error');
        return;
      }
      // The conversation row is created lazily on the user's first
      // message (see drainPersistQueue) — nothing to provision here.
      if (mode === 'chat') {
        conversation.startSession({ signedUrl: token.signedUrl, textOnly: true });
      } else {
        conversation.startSession({ signedUrl: token.signedUrl });
      }
    } catch (err) {
      if (err instanceof BudgetExhaustedError) {
        setRemainingSeconds(err.remainingSeconds);
        setBudgetSeconds(err.budgetSeconds);
        setPhase('rate-limited');
        track('limit_hit', { trigger: 'token', mode });
        return;
      }
      setErrorMsg((err as Error).message);
      setPhase('error');
      track('page_error', { source: 'token', mode, message: (err as Error).message });
    }
  }, [conversation, mode]);

  /**
   * Send a chat-mode user message. Pay-first ordering: debit the
   * bucket BEFORE pushing to the SDK. If the debit fails (rate-limit
   * 429, budget exhausted, network blip) the message is never sent,
   * so the user can't get free turns by spamming faster than the
   * server's min-interval lock. The previous order (send → debit) let
   * rapid-fire callers drain the bucket past zero — flagged in review
   * as the budget-bypass bug.
   *
   * The in-flight ref guard prevents a double-tap on Send from firing
   * two concurrent debits. Button-disabled state in ChatSessionView is
   * the primary UX, the ref is the correctness backstop.
   */
  const debitInFlightRef = useRef(false);

  const handleChatSend = useCallback(
    async (text: string) => {
      if (mode !== 'chat' || phase !== 'live' || !sessionTokenRef.current) return;
      if (debitInFlightRef.current) return;
      debitInFlightRef.current = true;
      // Render the user's message immediately (ChatGPT-style). The
      // ElevenLabs text channel never echoes user_message back as a
      // transcript, so — unlike voice (ASR) — the FE has to own both
      // rendering and persistence of chat-mode user turns. Rolled back
      // below if the debit fails and the message is never sent.
      const turnId = crypto.randomUUID();
      setTurns((prev) => [...prev, { id: turnId, role: 'user', text }]);
      try {
        const debit = await postChatDebit(sessionTokenRef.current);
        // Update both states in the same render pass. `setRemainingSeconds`
        // alone would work via the snap-effect, but that path is two async
        // render cycles — the brief window between renders lets the display
        // show a stale value. Setting both together is instantaneous.
        setRemainingSeconds(debit.remainingSeconds);
        setDisplaySeconds(debit.remainingSeconds);
        // Debit succeeded — this turn is paid for. Persist it (the
        // first user turn flips userTurnSeenRef, which triggers lazy
        // conversation creation + the backend auto-title), then send.
        setChatAwaitingReply(true);
        persistTurn('user', text);
        conversation.sendUserMessage(text);
        // If the debit drained the bucket to zero, this turn was the
        // user's last. Tear down so the next attempt doesn't even
        // reach the debit endpoint.
        if (debit.remainingSeconds <= 0) {
          conversation.endSession();
          setPhase('rate-limited');
          track('limit_hit', { trigger: 'chat-debit', mode });
        }
      } catch (err) {
        // Debit failed (429 throttle or network blip). The message was
        // never sent — roll the optimistic turn back out so the
        // transcript stays truthful, and don't lock the UI into
        // "awaiting reply". The min-interval lock lets the next
        // attempt through after CHAT_TURN_MIN_INTERVAL_MS.
        setTurns((prev) => prev.filter((t) => t.id !== turnId));
        // eslint-disable-next-line no-console
        console.warn('chat-debit failed; message not sent', err);
      } finally {
        debitInFlightRef.current = false;
      }
    },
    [conversation, mode, phase, persistTurn],
  );

  const stop = useCallback(() => {
    setPhase('ending');
    conversation.endSession();
  }, [conversation]);

  const reset = useCallback(() => {
    sessionTokenRef.current = null;
    conversationIdRef.current = null;
    resumingRef.current = false;
    userTurnSeenRef.current = false;
    persistQueueRef.current = [];
    setCurrentConversationId(null);
    setRemainingSeconds(null);
    setTurns([]);
    setErrorMsg(null);
    setPersistError(null);
    setChatAwaitingReply(false);
    setPhase('idle');
  }, []);

  // When the user picks a new mode from the toggle, also reset any
  // residual state so the next start runs clean. The toggle is only
  // available in idle phase, so we don't need to handle in-session.
  const handleModeChange = useCallback((next: SessionMode) => {
    setMode(next);
    track('mode_changed', { mode: next });
  }, []);

  // Voice mode primes the mic before connecting; chat mode skips
  // straight to the connect-and-start path.
  const onStartCTA = useCallback(() => {
    if (mode === 'voice') setPhase('priming');
    else void start();
  }, [mode, start]);

  /**
   * Pending resume target. Set when the user clicks a voice
   * conversation in the history drawer — we fetch the detail eagerly,
   * stash it here, and route through MicPrePrompt for the same
   * permission-explainer UX a fresh voice start uses. The MicPrePrompt
   * onAccept handler picks the right branch (resume vs fresh start)
   * based on whether this is set.
   */
  const [pendingResumeDetail, setPendingResumeDetail] =
    useState<ConversationDetailDTO | null>(null);

  /**
   * Inner half of the resume flow — given a fetched conversation
   * detail, set up state and start the SDK. Split from handleResume
   * so the priming branch can invoke it after MicPrePrompt accepts.
   */
  const proceedResume = useCallback(
    async (detail: ConversationDetailDTO) => {
      if (!authenticated) return;
      // Tear down any in-flight session before starting the resumed
      // one. Flag the imminent disconnect as a resume teardown so
      // onDisconnect doesn't route it through session-ended → reset().
      if (sessionTokenRef.current) {
        resumingRef.current = true;
        try {
          conversationRef.current.endSession();
        } catch {
          // endSession threw — no disconnect will fire to consume the
          // flag, so clear it now to avoid suppressing a later end.
          resumingRef.current = false;
        }
      }
      setErrorMsg(null);
      setPersistError(null);
      setChatAwaitingReply(false);
      sessionTokenRef.current = null;
      // Resuming an existing conversation: drop any half-buffered
      // turns from the prior session and mark the user as already
      // engaged so every new turn persists into the resumed row.
      persistQueueRef.current = [];
      userTurnSeenRef.current = true;
      conversationIdRef.current = detail.id;
      setCurrentConversationId(detail.id);
      setMode(detail.mode);
      setTurns(detail.messages.map((m) => ({ id: m.id, role: m.role, text: m.text })));
      if (detail.messages.length > 0) {
        resumeContextRef.current = buildResumeContext(detail.messages);
      }
      setPhase('connecting');
      try {
        const fp = getFingerprint();
        sessionStartAtRef.current = Date.now();
        firstWordAtRef.current = null;
        track('session_start', { mode: detail.mode, resume: true });
        const token = await fetchSessionToken(fp, undefined, detail.mode);
        sessionTokenRef.current = token.sessionToken;
        setRemainingSeconds(token.remainingSeconds);
        setBudgetSeconds(token.budgetSeconds);
        setChatTurnSeconds(token.chatTurnSeconds);
        if (token.signedUrl === MOCK_URL || token.signedUrl.startsWith('MOCK_')) {
          setErrorMsg('Agent not configured.');
          setPhase('error');
          return;
        }
        if (detail.mode === 'chat') {
          conversation.startSession({ signedUrl: token.signedUrl, textOnly: true });
        } else {
          conversation.startSession({ signedUrl: token.signedUrl });
        }
      } catch (err) {
        if (err instanceof BudgetExhaustedError) {
          setRemainingSeconds(err.remainingSeconds);
          setBudgetSeconds(err.budgetSeconds);
          setPhase('rate-limited');
          track('limit_hit', { trigger: 'token', mode: detail.mode, resume: true });
          return;
        }
        setErrorMsg((err as Error).message);
        setPhase('error');
        track('page_error', { source: 'resume', message: (err as Error).message });
      }
    },
    [authenticated, conversation],
  );

  const showWaitlist = phase === 'rate-limited';
  const inSession = phase === 'live' || phase === 'ending' || phase === 'session-ended';
  const [historyOpen, setHistoryOpen] = useState(false);

  /**
   * Drawer row click handler — closes the drawer, fetches the detail,
   * and routes either through MicPrePrompt (voice resume) or directly
   * into the session start (chat resume).
   *
   * Why fetch detail here rather than inside proceedResume: we need
   * the mode before we know whether to prime. The detail fetch is
   * cheap (single row + its messages) so the round-trip is hidden by
   * the connecting view.
   */
  const handleResume = useCallback(
    async (id: string) => {
      if (!authenticated) return;
      setHistoryOpen(false);
      // Synchronous phase flip — batched with the drawer close so the
      // user sees "connecting" the same render the drawer disappears.
      // No flash of stale content while the detail fetch races.
      setPhase('connecting');
      try {
        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error('no Privy access token');
        const detail = await getConversation(accessToken, id);
        if (detail.mode === 'voice') {
          // Route through MicPrePrompt — first-time voice users need
          // the permission explainer the same way fresh starts do.
          // The onMicAccept handler will read pendingResumeDetail and
          // invoke proceedResume(detail).
          setPendingResumeDetail(detail);
          setPhase('priming');
          return;
        }
        await proceedResume(detail);
      } catch (err) {
        setErrorMsg((err as Error).message);
        setPhase('error');
        track('page_error', { source: 'resume-detail', message: (err as Error).message });
      }
    },
    [authenticated, getAccessToken, proceedResume],
  );

  /**
   * MicPrePrompt accept handler. Branches on whether we're priming
   * for a fresh start or for a resume. Mounting the modal twice
   * (once per branch) would be tidier, but threading two callbacks
   * keeps the existing overlay JSX untouched.
   */
  const onMicAccept = useCallback(() => {
    if (pendingResumeDetail) {
      const detail = pendingResumeDetail;
      setPendingResumeDetail(null);
      void proceedResume(detail);
    } else {
      void start();
    }
  }, [pendingResumeDetail, proceedResume, start]);

  const onMicCancel = useCallback(() => {
    setPendingResumeDetail(null);
    setPhase('idle');
  }, []);

  return (
    <>
      {/* ── Navbar ──────────────────────────────────────────────────────── */}
      <header
        className={`fixed top-0 left-0 w-full z-50 border-b transition-all duration-300 ${
          isCallActive || phase === 'session-ended'
            ? 'bg-surface/95 backdrop-blur-md border-outline-variant'
            : scrolled
              ? 'bg-surface/95 backdrop-blur-md border-outline-variant'
              : 'bg-surface/60 backdrop-blur-sm border-transparent'
        }`}
      >
        {/* In session: full-bleed px-5 so logo aligns with sidebar. On landing: centered max-width. */}
        <div className={`flex justify-between items-center py-4 w-full ${
          isCallActive || phase === 'session-ended'
            ? 'px-5'
            : 'px-6 md:px-16 max-w-[1200px] mx-auto'
        }`}>
          <div className="flex items-center gap-3">
            {/* History trigger — only renders for signed-in users (the
                HistoryDrawer itself short-circuits when unauthenticated).
                Placed left of the logo so it associates with the left-
                slide drawer, mirroring ChatGPT/Claude UX. */}
            <HistoryTrigger onClick={() => setHistoryOpen(true)} />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/logo.png" alt="Just Ask Satoshi" className="w-7 h-7 object-contain shrink-0" />
            <span className="font-semibold text-sm text-on-surface tracking-tight">
              Just Ask Satoshi
            </span>
          </div>

          {phase === 'idle' && (
            <div className="flex items-center gap-6">
              <a
                href="#what-to-ask"
                className="hidden md:block font-mono text-[11px] tracking-widest text-on-surface-variant hover:text-on-surface transition-colors"
              >
                WHAT TO ASK
              </a>
              <AuthButton />
              <button
                onClick={onStartCTA}
                className="btn-cta-glow bg-primary-bright text-on-primary font-mono font-bold text-[11px] tracking-widest px-5 py-2.5 hover:bg-primary active:scale-95 transition-all"
              >
                {mode === 'chat' ? 'START CHAT' : 'TALK TO SATOSHI'}
              </button>
            </div>
          )}

          {isCallActive && (
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-primary-bright animate-pulse" />
              <span className="font-mono text-[10px] tracking-widest text-primary">
                IN CONVERSATION
              </span>
            </div>
          )}
        </div>
      </header>

      {/* Drawer mounts at the page level so it can render its full-screen
          backdrop without being clipped by the navbar's overflow. */}
      <HistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onResume={handleResume}
      />

      {/* ── Main ──────────────────────────────────────────────────────────── */}
      {/* Landing: min-h-screen so content can grow. Non-landing: h-screen overflow-hidden
          so the fixed session layout can't be scrolled regardless of prior scroll position. */}
      <main className={`bg-surface ${phase === 'idle' ? 'min-h-screen' : 'h-screen overflow-hidden'}`}>
        {(phase === 'idle' || phase === 'priming') && (
          <IdleView onStart={onStartCTA} mode={mode} onModeChange={handleModeChange} />
        )}
        {phase === 'connecting' && <ConnectingView />}
        {inSession && mode === 'voice' && (
          <SessionView
            isSpeaking={conversation.mode === 'speaking'}
            isEnding={phase === 'ending' || phase === 'session-ended'}
            onStop={stop}
            onNewSession={reset}
            remaining={displaySeconds}
            budget={budgetSeconds}
            turns={turns}
            online={online}
            persistError={persistError}
            currentConversationId={currentConversationId}
            onResume={handleResume}
          />
        )}
        {inSession && mode === 'chat' && (
          <ChatSessionView
            isReplying={chatAwaitingReply}
            isEnding={phase === 'ending' || phase === 'session-ended'}
            onSend={(text) => void handleChatSend(text)}
            onStop={stop}
            onNewSession={reset}
            remaining={displaySeconds}
            budget={budgetSeconds}
            chatTurnSeconds={chatTurnSeconds}
            turns={turns}
            online={online}
            persistError={persistError}
            currentConversationId={currentConversationId}
            onResume={handleResume}
          />
        )}
        {phase === 'error' && <ErrorView msg={errorMsg} onReset={reset} />}
      </main>

      {/* ── Overlays ────────────────────────────────────────────────────── */}
      {/* Mic preflight is voice-only; chat mode skips this overlay entirely
          via onStartCTA. */}
      {phase === 'priming' && mode === 'voice' && (
        <MicPrePrompt onAccept={onMicAccept} onCancel={onMicCancel} />
      )}
      {showWaitlist && (
        <WaitlistModal reason="budget-exhausted" onClose={reset} />
      )}
      {showEndBanner && (
        <SessionEndBanner onClose={() => setShowEndBanner(false)} />
      )}
    </>
  );
}

/**
 * Build the contextual-update payload sent to the agent on resume.
 * Format keeps things compact (the agent only needs enough state to
 * continue coherently, not a verbatim replay) and labels the roles
 * from the agent's POV — Satoshi sees himself as "You", the human as
 * "User". The closing instruction nudges the agent to pick up the
 * thread rather than re-introduce itself.
 *
 * Truncated to the most recent MAX_RESUME_CONTEXT_MESSAGES so a long
 * conversation doesn't blow the agent's context window. The opening
 * line tells the agent how many earlier turns were dropped, so it
 * knows not to pretend it has full memory.
 */
const MAX_RESUME_CONTEXT_MESSAGES = 50;

function buildResumeContext(
  messages: Array<{ role: 'user' | 'satoshi'; text: string }>,
): string {
  const truncated = messages.length > MAX_RESUME_CONTEXT_MESSAGES;
  const slice = truncated ? messages.slice(-MAX_RESUME_CONTEXT_MESSAGES) : messages;
  const header = truncated
    ? `Context — this is a continuation of an earlier conversation. The full history was ${messages.length} turns; the most recent ${slice.length} are shown below. ${messages.length - slice.length} earlier turns are omitted — acknowledge this if the user references something not in view.`
    : 'Context — this is a continuation of an earlier conversation. The history below is what was said previously:';
  const lines = [
    header,
    '',
    ...slice.map((m) => `${m.role === 'satoshi' ? 'You' : 'User'}: ${m.text}`),
    '',
    'Continue from here as if no interruption happened.',
  ];
  return lines.join('\n');
}

/**
 * Small navbar-left affordance to open the history drawer. Only renders
 * when Privy is configured AND the user is authenticated — anonymous
 * visitors see no extra chrome. Hidden mid-call to keep the in-session
 * navbar minimal (matches the existing in-call layout where only the
 * `IN CONVERSATION` indicator shows up).
 */
function HistoryTrigger({ onClick }: { onClick: () => void }) {
  const appId = process.env.NEXT_PUBLIC_PRIVY_APP_ID;
  if (!appId) return null;
  return <HistoryTriggerInner onClick={onClick} />;
}

function HistoryTriggerInner({ onClick }: { onClick: () => void }) {
  const { authenticated, ready } = usePrivy();
  if (!ready || !authenticated) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open history"
      className="text-on-surface-variant hover:text-on-surface transition-colors p-1.5 -ml-1.5"
    >
      {/* Three-line "history" glyph — minimal SVG to avoid pulling an
          icon library for one button. */}
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
        <line x1="4" y1="7" x2="20" y2="7" />
        <line x1="4" y1="12" x2="20" y2="12" />
        <line x1="4" y1="17" x2="14" y2="17" />
      </svg>
    </button>
  );
}
